import { StrictMode, useEffect, useState, type ReactNode } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
  useNavigate,
  useRouteError,
  type MetaFunction,
} from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App as AntApp } from "antd";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import axios from "axios";
import "antd/dist/reset.css";
import "./index.css";
import "./theme/theme.css";
import i18n, { loadLanguage } from "./i18n/i18n";
import { rememberLocale, shouldPrefixHref, splitLocalePath, withLocale } from "./i18n/localePath";
import { AuthProvider } from "./api/auth/AuthProvider";
import { ErrorBoundary as AppErrorBoundary } from "./components/ErrorBoundary";
import { I18nAntdProvider } from "./i18n/I18nAntdProvider";
import { useAmplitudeRouterTracking } from "./analytics/useAmplitudeRouterTracking";
import { findRouteKey, localeAlternateMeta, resolveRouteSeo, seoTagsToMeta } from "./seo/routeMeta";
import { jsonLdMeta, routeJsonLd } from "./seo/structuredData";

const GTM_ID = "GTM-NLMTC9GB";
const GA_ID = "G-TR04QJ0ZQ0";

const GTM_SNIPPET = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_ID}');`;

const GTAG_SNIPPET = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag("js", new Date());
gtag("config", "${GA_ID}");`;

const BREVO_SNIPPET = `window.Brevo = window.Brevo || [];
Brevo.push(["init", { client_key: "5t0bnd5bkoxucpmn2woleqqc" }]);`;

/** <html lang>, the language meta tag and og:locale for each URL locale. */
const LOCALE_META = {
  en: { name: "English", og: "en_US" },
  ru: { name: "Russian", og: "ru_RU" },
  uz: { name: "Uzbek", og: "uz_UZ" },
} as const;

/**
 * Server-rendered title, description, canonical, robots and social tags for
 * every URL. Routes with their own `meta` (stock pages, articles) override it.
 */
export const meta: MetaFunction = ({ location }) => {
  const pathname = location.pathname.replace(/\/+$/, "") || "/";
  // In the URL's language (the server has all three bundles loaded).
  const localeT = i18n.getFixedT(splitLocalePath(pathname).locale ?? "en");
  const t = (key: string) => localeT(key);
  const bare = splitLocalePath(pathname).pathname.replace(/\/+$/, "") || "/";
  const seo = resolveRouteSeo(pathname, t);
  return [
    ...seoTagsToMeta(seo),
    ...(seo.robots === "index, follow" ? localeAlternateMeta(pathname) : []),
    // Company graph on the home page; breadcrumbs and page types elsewhere.
    ...jsonLdMeta(
      routeJsonLd(findRouteKey(bare), pathname, seo.robots === "index, follow", t),
    ),
  ];
};

export function Layout({ children }: { children: ReactNode }) {
  const locale = splitLocalePath(useLocation().pathname).locale ?? "en";
  const localeMeta = LOCALE_META[locale];
  return (
    <html lang={locale}>
      <head>
        <meta charSet="UTF-8" />
        <script dangerouslySetInnerHTML={{ __html: GTM_SNIPPET }} />
        <meta name="yandex-verification" content="a273ade4f9888b4e" />
        <link rel="icon" type="image/webp" href="/Logomark.webp" />
        <link rel="icon" type="image/png" href="/Logomark.png" />
        <link rel="apple-touch-icon" href="/Logomark.png" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, viewport-fit=cover"
        />
        <meta name="author" content="Akinda" />
        <meta name="language" content={localeMeta.name} />
        <meta property="og:site_name" content="Akinda" />
        <meta property="og:locale" content={localeMeta.og} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@akinda" />
        <meta name="twitter:creator" content="@akinda" />
        <meta name="theme-color" content="#7F56D9" />
        <Meta />
        <script
          async
          src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        />
        <script dangerouslySetInnerHTML={{ __html: GTAG_SNIPPET }} />
        <script src="https://accounts.google.com/gsi/client" async defer />
        <link
          rel="preconnect"
          href="https://www.tradingview-widget.com"
          crossOrigin=""
        />
        <link rel="dns-prefetch" href="https://www.tradingview-widget.com" />
        <link rel="preconnect" href="https://s3.tradingview.com" crossOrigin="" />
        <link rel="dns-prefetch" href="https://s3.tradingview.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap"
          rel="stylesheet"
        />
        <script src="https://cdn.brevo.com/js/sdk-loader.js" async />
        <script dangerouslySetInnerHTML={{ __html: BREVO_SNIPPET }} />
        <Links />
      </head>
      <body>
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        // Retry once for network errors and 5xx. 4xx answers (auth, paywall,
        // not found) won't change on retry, so show them immediately.
        // Mutations keep TanStack's default of no retries.
        retry: (failureCount, error) =>
          failureCount < 1 &&
          !(
            axios.isAxiosError(error) &&
            error.response &&
            error.response.status < 500
          ),
      },
    },
  });

function RouteTracking() {
  useAmplitudeRouterTracking();
  return null;
}

/**
 * The URL is the language. The server renders it and entry.client switches
 * to it before hydrating; this keeps it in sync on later client navigations
 * to another locale. Clicks on unprefixed links keep the current locale.
 */
function LocaleFromUrl() {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = splitLocalePath(location.pathname).locale;
  if (typeof window !== "undefined" && locale) rememberLocale(locale);

  useEffect(() => {
    if (!locale) return;
    document.documentElement.lang = locale;
    const current = i18n.resolvedLanguage?.split("-")[0];
    if (current === locale && i18n.hasResourceBundle(locale, "translation")) return;
    void loadLanguage(locale).then(() => i18n.changeLanguage(locale));
  }, [locale]);

  useEffect(() => {
    if (!locale) return;
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const anchor = (event.target as Element | null)?.closest?.("a");
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const href = anchor.getAttribute("href");
      if (!href || !shouldPrefixHref(href)) return;
      event.preventDefault();
      navigate(withLocale(href, locale));
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [locale, navigate]);

  return null;
}

export default function App() {
  // One QueryClient per request on the server, one per page load in the browser.
  const [queryClient] = useState(createQueryClient);

  return (
    <StrictMode>
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AntApp>
              <I18nAntdProvider>
                <NuqsAdapter>
                  <RouteTracking />
                  <LocaleFromUrl />
                  <Outlet />
                </NuqsAdapter>
              </I18nAntdProvider>
            </AntApp>
          </AuthProvider>
        </QueryClientProvider>
      </AppErrorBoundary>
    </StrictMode>
  );
}

/** Last-resort boundary for errors thrown outside a route's own boundary. */
export function ErrorBoundary() {
  const error = useRouteError();
  const status = isRouteErrorResponse(error) ? error.status : 500;
  return (
    <main style={{ padding: 48, textAlign: "center" }}>
      <h1>{status === 404 ? "Page not found" : "Something went wrong"}</h1>
      <p>
        <a href="/">Go to the homepage</a>
      </p>
    </main>
  );
}
